// Bibliothèque de sons de scan (bip "valide" / "invalide") pour la fonction
// Dépôt. Les sons sont entièrement synthétisés via l'API Web Audio (aucun
// fichier audio à héberger) : chacun est décrit par une courte séquence de
// tonalités (fréquence, décalage de départ, durée, forme d'onde). L'opérateur
// choisit son couple de sons + le volume depuis le bouton "Scanner prêt" de la
// page Dépôt ; le réglage est mémorisé sur cet appareil (localStorage), comme
// le thème clair/sombre (voir context/ThemeContext.jsx).

const STORAGE_KEY = "scanSoundSettings";

// Chaque son : { id, label, tones: [{ freq, start, duration, type }] }
// `start`/`duration` en secondes, relatifs au déclenchement du son.
export const VALID_SOUNDS = [
  { id: "bip-classique", label: "Bip classique", tones: [{ freq: 1500, start: 0, duration: 0.09, type: "sine" }] },
  { id: "double-bip", label: "Double bip", tones: [
    { freq: 1500, start: 0, duration: 0.06, type: "sine" },
    { freq: 1900, start: 0.09, duration: 0.06, type: "sine" },
  ] },
  { id: "montee", label: "Montée", tones: [
    { freq: 900, start: 0, duration: 0.05, type: "triangle" },
    { freq: 1300, start: 0.05, duration: 0.05, type: "triangle" },
    { freq: 1800, start: 0.1, duration: 0.07, type: "triangle" },
  ] },
  { id: "clochette", label: "Clochette", tones: [
    { freq: 2000, start: 0, duration: 0.12, type: "sine" },
    { freq: 3000, start: 0.02, duration: 0.12, type: "sine" },
  ] },
  { id: "synthe-doux", label: "Synthé doux", tones: [
    { freq: 1200, start: 0, duration: 0.16, type: "sine" },
  ] },
];

export const INVALID_SOUNDS = [
  { id: "double-grave", label: "Double bip grave", tones: [
    { freq: 220, start: 0, duration: 0.15, type: "sine" },
    { freq: 220, start: 0.18, duration: 0.15, type: "sine" },
  ] },
  { id: "buzz", label: "Buzz", tones: [{ freq: 130, start: 0, duration: 0.3, type: "sawtooth" }] },
  { id: "descente", label: "Descente", tones: [
    { freq: 800, start: 0, duration: 0.08, type: "triangle" },
    { freq: 500, start: 0.08, duration: 0.08, type: "triangle" },
    { freq: 250, start: 0.16, duration: 0.12, type: "triangle" },
  ] },
  { id: "alarme", label: "Alarme courte", tones: [
    { freq: 400, start: 0, duration: 0.08, type: "square" },
    { freq: 300, start: 0.1, duration: 0.08, type: "square" },
    { freq: 400, start: 0.2, duration: 0.08, type: "square" },
  ] },
  { id: "toc-toc", label: "Toc-toc", tones: [
    { freq: 180, start: 0, duration: 0.05, type: "square" },
    { freq: 180, start: 0.09, duration: 0.05, type: "square" },
  ] },
];

// Bip "quantité pas encore complète" (article reconnu, mais il en faut
// encore) — délibérément COURT et NEUTRE, nettement différent du bip
// "complet" (VALID_SOUNDS, qui doit rester associé à "cette ligne est finie")
// et du bip d'erreur (INVALID_SOUNDS). Sert de simple accusé de réception :
// "scan pris en compte, continue".
export const PARTIAL_SOUNDS = [
  { id: "tic-court", label: "Tic court", tones: [{ freq: 1000, start: 0, duration: 0.04, type: "sine" }] },
  { id: "clic-double", label: "Clic double rapide", tones: [
    { freq: 1100, start: 0, duration: 0.03, type: "square" },
    { freq: 1100, start: 0.05, duration: 0.03, type: "square" },
  ] },
  { id: "pop", label: "Pop", tones: [{ freq: 700, start: 0, duration: 0.05, type: "triangle" }] },
  { id: "grave-court", label: "Grave court", tones: [{ freq: 500, start: 0, duration: 0.05, type: "sine" }] },
];

const DEFAULT_SETTINGS = {
  validSoundId: VALID_SOUNDS[0].id,
  partialSoundId: PARTIAL_SOUNDS[0].id,
  invalidSoundId: INVALID_SOUNDS[0].id,
  // Volume par défaut relevé par rapport à l'ancien réglage fixe (0.15), jugé
  // trop bas par les utilisateurs en environnement de dépôt (bruit ambiant).
  volume: 0.7,
};

export function getScanSoundSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return {
      validSoundId: VALID_SOUNDS.some((s) => s.id === parsed.validSoundId) ? parsed.validSoundId : DEFAULT_SETTINGS.validSoundId,
      partialSoundId: PARTIAL_SOUNDS.some((s) => s.id === parsed.partialSoundId) ? parsed.partialSoundId : DEFAULT_SETTINGS.partialSoundId,
      invalidSoundId: INVALID_SOUNDS.some((s) => s.id === parsed.invalidSoundId) ? parsed.invalidSoundId : DEFAULT_SETTINGS.invalidSoundId,
      volume: typeof parsed.volume === "number" && parsed.volume >= 0 && parsed.volume <= 1 ? parsed.volume : DEFAULT_SETTINGS.volume,
    };
  } catch (e) {
    return { ...DEFAULT_SETTINGS };
  }
}

export function setScanSoundSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (e) { /* stockage indisponible, réglage non persisté pour cette session */ }
}

// Joue une séquence de tonalités avec un multiplicateur de volume (0 à 1).
function playTones(tones, volume) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const peakGain = Math.max(0, Math.min(1, volume)) * 0.5; // 0.5 = pleine échelle confortable
    tones.forEach(({ freq, start, duration, type }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type || "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(Math.max(peakGain, 0.0001), ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + duration);
    });
    const totalDuration = Math.max(...tones.map((t) => t.start + t.duration), 0);
    setTimeout(() => ctx.close(), (totalDuration + 0.4) * 1000);
  } catch (e) { /* audio non disponible, ignoré */ }
}

function _bankFor(status) {
  if (status === "valid" || status === true) return VALID_SOUNDS;
  if (status === "partial") return PARTIAL_SOUNDS;
  return INVALID_SOUNDS; // "invalid", false, ou toute autre valeur
}

function _soundIdKeyFor(status) {
  if (status === "valid" || status === true) return "validSoundId";
  if (status === "partial") return "partialSoundId";
  return "invalidSoundId";
}

// Joue le bip actuellement choisi pour l'un des TROIS états d'un scan :
//   - "valid"   : article reconnu ET quantité désormais complète pour cette ligne
//   - "partial" : article reconnu, mais il en manque encore (continuer à scanner)
//   - "invalid" : mauvais article / erreur
// Compatibilité ascendante : `true`/`false` restent acceptés et équivalent
// respectivement à "valid"/"invalid" (aucun appelant existant ne connaissait
// encore l'état "partial").
export function playScanSound(status) {
  const settings = getScanSoundSettings();
  const bank = _bankFor(status);
  const chosen = bank.find((s) => s.id === settings[_soundIdKeyFor(status)]) || bank[0];
  playTones(chosen.tones, settings.volume);
}

// Prévisualise un son précis (utilisé par le bouton "Tester" du menu de réglages).
export function previewSound(soundId, status, volume) {
  const bank = _bankFor(status);
  const chosen = bank.find((s) => s.id === soundId) || bank[0];
  playTones(chosen.tones, volume);
}
